import { PropsWithChildren, JSX, ReactElement, CSSProperties } from 'react'

import classNames from 'classnames'

import { theme } from 'styles/theme'

import * as styles from './styles.scss'

type Variant =
    | 'body-xsmall'
    | 'body-small'
    | 'body-medium'
    | 'body-main'
    | 'body-plus'
    | 'heading-1'
    | 'heading-2'
    | 'heading-3'
    | 'heading-4'
    | 'heading-5'

type TextColor =
    | 'main-heading'
    | 'primary'
    | 'base'
    | 'muted'
    | 'light'
    | 'lighter'
    | 'extra-light'
    | 'disabled'

type TextWeight =
    | 'extra-light'
    | 'light'
    | 'normal'
    | 'medium'
    | 'semi-bold'
    | 'bold'

type SemanticStyle = 'page-heading' | 'default'

interface AppTypographyBaseProps {
    variant?: Variant
    semanticStyle?: SemanticStyle
    as?: keyof JSX.IntrinsicElements
    noGutter?: boolean
    className?: string
    style?: CSSProperties
    title?: string
    color?: TextColor
    weight?: TextWeight
    italic?: boolean
}

type AppTypographyProps = PropsWithChildren<AppTypographyBaseProps>

const getDefaultTag = (variant?: Variant): keyof JSX.IntrinsicElements => {
    if (!variant) {
        return 'span'
    }
    if (variant.startsWith('heading-')) {
        switch (variant) {
            case 'heading-1':
                return 'h1'
            case 'heading-2':
                return 'h2'
            case 'heading-3':
                return 'h3'
            case 'heading-4':
                return 'h4'
            case 'heading-5':
                return 'h5'
        }
    }
    if (variant.startsWith('body-')) {
        return 'p'
    }
    return 'span'
}

function getColorValue(color?: TextColor): string | undefined {
    const { colors } = theme

    switch (color) {
        case 'main-heading':
            return colors.textMainHeading
        case 'primary':
            return colors.textPrimary
        case 'base':
            return colors.textBase
        case 'muted':
            return colors.textMuted
        case 'light':
            return colors.textLight
        case 'lighter':
            return colors.textLighter
        case 'extra-light':
            return colors.textExtraLight
        case 'disabled':
            return colors.textDisabled
    }
}

function getWeightValue(weight?: TextWeight): any {
    const { fontWeight } = theme

    switch (weight) {
        case 'extra-light':
            return fontWeight.extraLight
        case 'light':
            return fontWeight.light
        case 'normal':
            return fontWeight.regular
        case 'medium':
            return fontWeight.semiBold
        case 'semi-bold':
            return fontWeight.semiBold
        case 'bold':
            return fontWeight.bold
    }
}

export const AppTypography = ({
    variant = 'body-main',
    semanticStyle = 'default',
    as,
    className,
    children,
    noGutter = false,
    style,
    title,
    color,
    weight,
    italic,
}: AppTypographyProps): ReactElement => {
    const Tag = as || getDefaultTag(variant)

    let additionalStyles: CSSProperties | undefined = noGutter
        ? { marginBottom: 0 }
        : undefined

    if (color) {
        additionalStyles = {
            ...additionalStyles,
            color: getColorValue(color),
        }
    }

    if (weight) {
        additionalStyles = {
            ...additionalStyles,
            fontWeight: getWeightValue(weight),
        }
    }

    if (italic) {
        additionalStyles = {
            ...additionalStyles,
            fontStyle: 'italic',
        }
    }

    return (
        <Tag
            className={classNames(className, {
                [styles[variant]]: !!variant,
                [styles[semanticStyle]]: !!semanticStyle,
            })}
            style={style ? { ...style, ...additionalStyles } : additionalStyles}
            title={title}
        >
            {children}
        </Tag>
    )
}
